// InventorySlot + InventoryGrid + the ONE shared SplitPopover.
// Drag-drop emits drag_drop and never predicts — the item moves when a patch
// says so. Right-click a stack = split popover. Left-click selects the item
// into the info pane. Empty gear slots draw a dim ghost glyph of what goes
// there. Durability renders as the one thin underbar idiom.

import { el, tooltip, clear } from '../kit';
import { sendEvent } from '../net';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';

export interface SlotOpts {
  locked?: boolean;
  space?: boolean; // container-handle slot (swap the bag itself)
  ghost?: string; // gear slot type hint when empty
  tip?: string; // tooltip override (also binds on empty slots, e.g. reserve sub-slots)
}

function eventSlot(key: string): string | number {
  return /^\d+$/.test(key) ? Number(key) : key;
}

// The ghost glyphs — dim monotone outline drawings (never item art).
const GHOST_PATHS: Record<string, string> = {
  head: 'M6 22 A10 10 0 0 1 26 22 M6 22 v5 M26 22 v5 M16 22 v6',
  body: 'M8 6 L13 9 L19 9 L24 6 L22 16 L21 26 L11 26 L10 16 Z',
  legs: 'M10 6 L22 6 L21 26 L18 26 L16 12 L14 26 L11 26 Z',
  main_hand: 'M8 24 L24 8 M11 21 L15 25 M13 23 L9 27',
  off_hand: 'M7 7 L25 7 L24 17 L16 26 L8 17 Z',
};

export function slotNode(inv: string, key: string, data: any, opts: SlotOpts = {}): HTMLElement {
  const hasItem = data && typeof data === 'object' && data.item;
  const node = el('div', {
    // 'ghost' only while EMPTY — the dim ghost-glyph style must never touch a worn item's art
    class: 'inv-slot' + (hasItem || opts.locked ? '' : ' empty') + (!hasItem && opts.ghost ? ' ghost' : ''),
    dataset: { inv, slot: key },
  });
  if (opts.locked) {
    node.style.opacity = '0.5';
    node.append(el('div', { class: 'k-well', style: 'position:absolute;inset:0' }));
    node.append(
      el('div', {
        style:
          'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;' +
          'color:var(--text-faint);font-size:16px',
        text: '🔒',
      }),
    );
    return node;
  }
  if (hasItem || opts.space) {
    const well = el('div', { class: 'k-well', style: 'position:absolute;inset:0' });
    if (opts.space) well.style.borderColor = 'var(--border-light)';
    node.append(well);
  }
  if (hasItem) {
    const icon = icons.texture(String(data.item), 34);
    icon.style.position = 'absolute';
    icon.style.inset = '4px 4px 8px 4px';
    icon.style.width = 'auto';
    icon.style.height = 'auto';
    node.append(icon);
    const count = Number(data.count ?? 1);
    if (count > 1) node.append(el('div', { class: 'count', text: String(count) }));
    const dura = data.durability;
    if (dura && Number(dura.max) > 0) {
      const frac = Math.max(0, Math.min(1, Number(dura.cur) / Number(dura.max)));
      node.append(
        el(
          'div',
          { class: 'underbar' + (frac < 0.25 ? ' low' : '') },
          el('div', { style: `width:${frac * 100}%` }),
        ),
      );
    }
    let tip = String(data.name ?? vsj.info(String(data.item))?.name ?? data.item);
    if (dura && Number(dura.max) > 0) tip += `  (${dura.cur}/${dura.max})`;
    tooltip(node, () => opts.tip ?? tip);

    node.addEventListener('click', () => ui.select(String(data.item), { inv, slot: eventSlot(key) }));
    node.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      if (count > 1) openSplit(inv, eventSlot(key), node);
    });

    node.draggable = true;
    node.addEventListener('dragstart', (e) => {
      e.dataTransfer!.setData(
        'application/x-stoneheim-slot',
        JSON.stringify({ inv, slot: eventSlot(key) }),
      );
      e.dataTransfer!.effectAllowed = 'move';
    });
  } else if (opts.ghost) {
    node.append(
      el('div', { class: 'k-well', style: 'position:absolute;inset:0' }),
      ghostGlyph(opts.ghost),
    );
    tooltip(node, () => `${opts.ghost!.replace(/_/g, ' ')} — nothing worn`);
  } else if (opts.tip) {
    tooltip(node, () => opts.tip!);
  }

  // every slot (incl. empty) is a live indexed drop target
  node.addEventListener('dragover', (e) => {
    if (e.dataTransfer?.types.includes('application/x-stoneheim-slot')) {
      e.preventDefault();
      node.classList.add('drop-hover');
    }
  });
  node.addEventListener('dragleave', () => node.classList.remove('drop-hover'));
  node.addEventListener('drop', (e) => {
    e.preventDefault();
    node.classList.remove('drop-hover');
    const raw = e.dataTransfer?.getData('application/x-stoneheim-slot');
    if (!raw) return;
    try {
      const src = JSON.parse(raw);
      sendEvent('drag_drop', { src, dest: { inv, slot: eventSlot(key) } });
    } catch {
      /* malformed drag payload — ignore */
    }
  });
  return node;
}

/** First empty slot index in bag <i> per SERVED state, or null when full/absent.
 * Used by the bag-tab drop targets (native DnD + touchdnd) so a drop on a
 * non-visible bag's tab lands in its first free slot via plain drag_drop. */
export function firstFreeBagSlot(bagIndex: number): number | null {
  const slots = vsj.at(`bags.${bagIndex}.slots`);
  if (!slots || typeof slots !== 'object') return null;
  const keys = Object.keys(slots)
    .filter((k) => /^\d+$/.test(k))
    .sort((a, b) => Number(a) - Number(b));
  for (const k of keys) if (slots[k] == null) return Number(k);
  return null;
}

/** drag_drop dest for a drop on bag TAB <i> per SERVED state (both DnD paths):
 * empty bagspace → its equip slot (equips the dropped bag); equipped bag →
 * its first free slot; full bag → null (caller may just switch tabs). */
export function bagTabDropDest(i: number): { inv: string; slot: number } | null {
  if (vsj.at(`bags.${i}.bag`) == null) return { inv: 'bagspace', slot: i };
  const free = firstFreeBagSlot(i);
  return free == null ? null : { inv: `bag${i}`, slot: free };
}

function ghostGlyph(kind: string): HTMLElement {
  const path = GHOST_PATHS[kind] ?? '';
  const wrap = el('div', {
    style: 'position:absolute;inset:6px;display:flex;align-items:center;justify-content:center',
  });
  wrap.innerHTML =
    `<svg viewBox="0 0 32 32" width="100%" height="100%" fill="none" ` +
    `stroke="var(--ghost-text)" stroke-width="1.6" stroke-linejoin="round">` +
    `<path d="${path}"/></svg>`;
  return wrap;
}

/** Render a slots record {"0": Slot|null, ...} into a grid element. */
export function bindGrid(
  gridEl: HTMLElement,
  inv: string,
  slots: Record<string, any>,
  opts: { columns?: number; lockedFrom?: number } = {},
): void {
  clear(gridEl);
  gridEl.classList.add('inv-grid');
  if (opts.columns)
    gridEl.style.gridTemplateColumns = `repeat(${opts.columns}, var(--slot-size))`;
  const keys = Object.keys(slots).sort((a, b) => {
    const an = /^\d+$/.test(a), bn = /^\d+$/.test(b);
    if (an && bn) return Number(a) - Number(b);
    return a < b ? -1 : 1;
  });
  for (const k of keys) {
    const locked = opts.lockedFrom != null && /^\d+$/.test(k) && Number(k) >= opts.lockedFrom;
    gridEl.append(slotNode(inv, k, slots[k], { locked }));
  }
}

// ---------- split popover (one shared instance, input never clobbered) ----------
let splitEl: HTMLElement | null = null;
let splitState = { inv: '', slot: 0 as string | number };
let splitTitle: HTMLElement, allBtn: HTMLButtonElement, halfBtn: HTMLButtonElement;
let nInput: HTMLInputElement;

function splitSlotData(): any {
  const { inv, slot } = splitState;
  let slots: any = null;
  if (inv.startsWith('bag')) slots = vsj.at(`bags.${inv.slice(3)}.slots`);
  else if (inv === 'toolbag') slots = vsj.at('toolbag.slots');
  else if (inv === 'toolbagres') slots = vsj.at('toolbag.res');
  else if (inv === 'ground') slots = vsj.at('ground.slots');
  return slots?.[String(slot)];
}

function ensureSplit(): void {
  if (splitEl) return;
  splitTitle = el('div');
  allBtn = el('button', { class: 'k-btn', style: 'flex:1' });
  halfBtn = el('button', { class: 'k-btn', style: 'flex:1' });
  nInput = el('input', { class: 'k-input', placeholder: 'count', style: 'flex:1' });
  const send = (mode: string, n?: number) => {
    const args: Record<string, unknown> = { inv: splitState.inv, slot: splitState.slot, mode };
    if (mode === 'n') args.n = n;
    sendEvent('split', args);
    hideSplit();
  };
  allBtn.onclick = () => send('all');
  halfBtn.onclick = () => send('half');
  const sendN = () => {
    const n = Number(nInput.value.trim());
    if (Number.isInteger(n) && n >= 1) send('n', n);
  };
  nInput.addEventListener('keydown', (e) => e.key === 'Enter' && sendN());
  splitEl = el(
    'div',
    {
      class: 'k-panel',
      style:
        'position:fixed;z-index:70;width:240px;padding:10px;display:none;' +
        'flex-direction:column;gap:6px;background:var(--bg-modal);border-color:var(--border-light)',
    },
    splitTitle,
    el('div', { style: 'display:flex;gap:6px' }, allBtn, halfBtn),
    el(
      'div',
      { style: 'display:flex;gap:6px' },
      nInput,
      el('button', { class: 'k-btn', text: 'Split', onclick: sendN }),
    ),
  );
  document.body.append(splitEl);
  document.addEventListener('pointerdown', (e) => {
    if (splitEl && splitEl.style.display !== 'none' && !splitEl.contains(e.target as Node))
      hideSplit();
  });
  vsj.onChange(() => {
    if (splitEl && splitEl.style.display !== 'none') syncSplit();
  });
}

function syncSplit(): void {
  const s = splitSlotData();
  const count = Number(s?.count ?? 0);
  if (!s || count < 2) {
    hideSplit();
    return;
  }
  const name = String(s.name ?? vsj.info(String(s.item))?.name ?? s.item);
  splitTitle.textContent = `Split ${name} ×${count}`;
  allBtn.textContent = `All (${count})`;
  halfBtn.textContent = `Half (${Math.floor(count / 2)})`;
}

export function openSplit(inv: string, slot: string | number, anchor: HTMLElement): void {
  ensureSplit();
  splitState = { inv, slot };
  syncSplit();
  if (!splitSlotData()) return;
  const r = anchor.getBoundingClientRect();
  splitEl!.style.display = 'flex';
  const w = 240;
  splitEl!.style.left = `${Math.min(r.left, innerWidth - w - 8)}px`;
  splitEl!.style.top = `${Math.min(r.bottom + 4, innerHeight - 130)}px`;
  nInput.focus();
}

function hideSplit(): void {
  if (splitEl) splitEl.style.display = 'none';
}
